// A project's Field settings (N6, 2026-10-14; the project's own fields since task F,
// 2026-10-20): the project card's, its studies' and its datasets' additional fields. The
// project's curators and admins (`project.can_manage`) create, edit, deactivate and delete them,
// and copy templates in ("Add from templates"); members read them. A change here never reaches
// another project. The editor is shared with Management -> Field templates
// (forms/FieldDefinitionEditor.jsx lists its calls), plus:
//
//   GET  /api/projects/{id}/field-templates        the active templates, each with `copied`
//   POST /api/projects/{id}/fields/from-template   {template_ids}: a copy of each in the project
import { useState } from "react";
import { api } from "../api";
import { Empty, ErrorNote, Loading } from "../components/Feedback";
import { Btn, Card } from "../components/Shared";
import { useApi } from "../useApi";
import FieldDefinitionEditor from "../forms/FieldDefinitionEditor";
import { FIELD_TARGETS, typeLabel } from "../forms/fieldTypes";
import { LEGEND } from "./admin/parts";

const CHECK_ROW = { display: "inline-flex", gap: 8, alignItems: "flex-start", fontSize: 14, color: "#1a1a1a" };

// "Add from templates": the templates not copied yet, ticked and copied in one call.
function AddFromTemplates({ project, onCopied, onClose }) {
  const templates = useApi(`/projects/${project.id}/field-templates`);
  const [chosen, setChosen] = useState(() => new Set());
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const toggle = id => setChosen(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const open = (Array.isArray(templates.data) ? templates.data : []).filter(t => !t.copied);

  const copy = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/projects/${project.id}/fields/from-template`, { template_ids: [...chosen] });
      onCopied(open.filter(t => chosen.has(t.id)).map(t => t.label));
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Card style={{ marginBottom: 20, padding: "20px 24px" }}>
      <form onSubmit={copy} aria-label="Add from templates" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Add from templates</h3>
        <p style={{ margin: 0, fontSize: 13, color: "#4b5563" }}>
          A copy of each ticked template becomes a field of this project. You can change the copy here
          afterwards; later changes to the template do not reach it.
        </p>
        <ErrorNote error={error || templates.error} />
        {templates.loading && <Loading />}
        {!templates.loading && open.length === 0 && <Empty>There are no templates left to copy.</Empty>}
        {FIELD_TARGETS.map(t => {
          const rows = open.filter(x => x.target === t.value);
          if (!rows.length) return null;
          return (
            <fieldset key={t.value} style={{ border: "none", margin: 0, padding: 0 }}>
              <legend style={LEGEND}>{t.plural}</legend>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {rows.map(x => (
                  <label key={x.id} style={CHECK_ROW}>
                    <input type="checkbox" checked={chosen.has(x.id)} onChange={() => toggle(x.id)} />
                    <span>
                      {x.label} <span style={{ fontSize: 12, color: "#6b7280" }}>({typeLabel(x.field_type)}
                      {(x.options || []).length > 0 && `: ${x.options.join(", ")}`})</span>
                      {x.help_text && <span style={{ display: "block", fontSize: 12, color: "#4b5563" }}>{x.help_text}</span>}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          );
        })}
        <div style={{ display: "flex", gap: 10 }}>
          <Btn type="submit" disabled={busy || chosen.size === 0}>Copy into this project</Btn>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        </div>
      </form>
    </Card>
  );
}

export default function ProjectFieldSettings({ project, onSaved, onClose }) {
  const canEdit = !!project.can_manage;
  const [adding, setAdding] = useState(false);
  const [version, setVersion] = useState(0);     // remounts the editor after a copy
  const [notice, setNotice] = useState(null);

  const changed = () => { if (onSaved) onSaved(); };
  const copied = labels => {
    setAdding(false);
    setNotice(`Copied into this project: ${labels.join(", ")}.`);
    setVersion(v => v + 1);
    changed();
  };

  return (
    <Card style={{ marginBottom: 24 }}>
      <section aria-labelledby="field-settings-title" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <h2 id="field-settings-title" style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Field settings</h2>
        <p style={{ margin: 0, fontSize: 13, color: "#4b5563" }}>
          The additional fields of this project: of its project card, its studies and its datasets. A study or
          dataset field applies to every study or dataset in the project. The fields belong to this project
          alone; changing them changes no other project. A field that has values is deactivated rather than
          deleted, so its values stay readable.
        </p>
        {notice && <p role="status" style={{ margin: 0, fontSize: 14, color: "var(--dp-accent-dark)" }}>{notice}</p>}
        {canEdit && !adding && (
          <div><Btn variant="secondary" onClick={() => { setNotice(null); setAdding(true); }}>Add from templates</Btn></div>
        )}
        {adding && <AddFromTemplates project={project} onCopied={copied} onClose={() => setAdding(false)} />}
        <FieldDefinitionEditor key={version} base={`/projects/${project.id}/fields`} kind="project"
                               canEdit={canEdit} onChanged={changed} />
        <div><Btn variant="secondary" onClick={onClose}>Close</Btn></div>
      </section>
    </Card>
  );
}
